import { pick } from 'es-toolkit';

import {
  Template,
  TemplateEditOrCreateDTO,
} from '../services/TemplateService/Template.type';

type OptionalKeys<T> = {
  [K in keyof T]-?: Record<string, never> extends Pick<T, K> ? K : never;
}[keyof T];

type OptionalOnly<T> = Pick<T, OptionalKeys<T>>;

type TemplateUpdateBody = Omit<
  TemplateEditOrCreateDTO,
  'createdAt' | 'creator' | 'members' | 'rounds' | 'gameIds' | 'clonedFromTemplateId'
>;

type TemplateUpdateBodyOptionalOnly = OptionalOnly<TemplateUpdateBody>;

const OptionalTemplateUpdateProperties = [
  'activeGame',
  'aiContent',
  'attachments',
  'coverImageAltText',
  'coverImageId',
  'currentRound',
  'description',
  'id',
  'isBlockedFromSharing',
  'isContentFilterEnabled',
  'isPrivate',
  'language',
  'metadata',
  'parentId',
  'requireRealNames',
  'slides',
  'templateSharingPublic',
  'title',
  'settings',
] as const;

// define default values for all optional properties
// so that our optimistic update in the query cache
// reflects what the server is going to give us back
const defaultValuesForOptionalProperties: Required<TemplateUpdateBodyOptionalOnly> = {
  activeGame: null,
  aiContent: null,
  attachments: [],
  coverImageAltText: undefined as any,
  coverImageId: undefined as any,
  currentRound: null,
  description: null,
  id: null,
  isBlockedFromSharing: false,
  isContentFilterEnabled: true,
  isPrivate: true,
  language: null,
  metadata: null,
  parentId: null,
  requireRealNames: true,
  slides: [],
  templateSharingPublic: false,
  title: null,
  settings: null,
};

type UpdatedableTemplate = Omit<
  Template,
  'createdAt' | 'creator' | 'members' | 'clonedFromTemplateId' | 'content'
> & { content: Pick<Template['content'], 'slides' | 'activeGame'> };

export function buildLessonUpdateBodyFromDraftTemplate(
  draftTemplate: UpdatedableTemplate,
): TemplateEditOrCreateDTO {
  // first, turn the template into a subset of a TemplateEditOrCreateDTO
  const draft = pick(
    {
      ...draftTemplate,
      slides: draftTemplate.content.slides ?? [],
      ...('activeGame' in draftTemplate.content && {
        activeGame: draftTemplate.content.activeGame ?? null,
      }),
    },
    OptionalTemplateUpdateProperties,
  );

  const optionalProperties = OptionalTemplateUpdateProperties.filter(
    // only include properties that are in the draft
    key => key in draft,
  ).reduce(
    (acc, key) => ({
      ...acc,
      // null coalescing to the default value helps us avoid inconsistent nullish values
      // that would lead to re-running the mutation
      // i.e. if we send { foo: undefined } in the request, write an optimistic update of { foo: undefined }
      // to the cache, and then the mutation response has { foo: null },
      // all of a sudden our autosave logic tells us we have to update the server.
      // instead, we use defaultValuesForOptionalProperties to enforce consistency
      [key]: draft[key] ?? defaultValuesForOptionalProperties[key],
    }),
    {} as TemplateUpdateBodyOptionalOnly,
  );

  return {
    modifiedAt: draftTemplate.modifiedAt,
    ...optionalProperties,
  };
}

export function buildTemplateResponseFromTemplateEditOrCreateDTO(
  templateEditOrCreateDTO: TemplateEditOrCreateDTO,
  template: Template,
): Template {
  const {
    slides: mutationUpdatesSlides,
    activeGame: mutationUpdatesActiveGame,
    ...mutationUpdates
  } = OptionalTemplateUpdateProperties.filter(
    key => key in templateEditOrCreateDTO,
  ).reduce(
    (acc, key) => ({ ...acc, [key]: templateEditOrCreateDTO[key] }),
    {} as TemplateUpdateBody,
  );

  return {
    ...template,
    ...mutationUpdates,
    id: template.id,
    isPrivate: mutationUpdates.isPrivate ?? template.isPrivate,
    aiContent: mutationUpdates.aiContent ?? template.aiContent,
    modifiedAt: templateEditOrCreateDTO.modifiedAt,
    content: {
      ...template.content,
      ...(mutationUpdatesSlides && { slides: mutationUpdatesSlides }),
      ...(mutationUpdatesActiveGame !== undefined && {
        activeGame: mutationUpdatesActiveGame,
      }),
    },
  };
}
